import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, BadgeCheck, FileText, Quote,
  CheckCircle2, Database, History, Sparkles, Loader2, RefreshCw, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { thesisApi } from '@/api/thesisApi';
import { toast } from 'sonner';

/**
 * AI Transparency Panel — visible sidebar showing per-chapter trust signals:
 *
 *   - AI generation metadata (model, chunks used, language, timestamps)
 *   - Citation verification stats (verified / suspect / fabricated)
 *   - Unsupported paragraph count
 *   - Latest compliance score (if any)
 *   - Latest claim-safety classification (if any)
 *   - Version history depth
 *   - Human approval state
 *
 * The data is loaded from GET /api/thesis/chapters/{id}/transparency.
 */
export default function AITransparencyPanel({ userId, chapter, onChapterUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [safetyChecking, setSafetyChecking] = useState(false);

  const load = useCallback(async () => {
    if (!chapter?.id) return;
    setLoading(true);
    try {
      const t = await thesisApi.getChapterTransparency(userId, chapter.id);
      setData(t);
    } catch (e) {
      console.error('Transparency load failed', e);
    } finally {
      setLoading(false);
    }
  }, [userId, chapter?.id]);

  useEffect(() => { load(); }, [load]);

  const runSafety = async () => {
    setSafetyChecking(true);
    try {
      await thesisApi.safetyCheckChapter(userId, chapter.id);
      toast.success('Safety check complete');
      await load();
    } catch (e) {
      toast.error('Safety check failed');
    } finally {
      setSafetyChecking(false);
    }
  };

  const toggleApprove = async () => {
    const next = !data?.human_review?.approved;
    try {
      await thesisApi.approveChapter(userId, chapter.id, next);
      toast.success(next ? 'Chapter marked as human-approved' : 'Approval revoked');
      await load();
      onChapterUpdated && onChapterUpdated();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  if (!chapter) {
    return (
      <div className="text-sm text-muted-foreground p-4">
        Select a chapter to see its AI transparency snapshot.
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="transparency-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading transparency...
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-6 text-sm text-muted-foreground">No data yet.</div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ai-transparency-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#1d4ed8] dark:text-[#7aa9ff]" />
          <h3 className="font-serif text-base font-semibold text-foreground" style={{ fontFamily: '"Fraunces", serif' }}>
            AI Transparency
          </h3>
        </div>
        <Button variant="ghost" size="icon" onClick={load} className="h-7 w-7" data-testid="transparency-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* AI Generation card */}
      <Section title="AI generation" icon={Sparkles}>
        <KeyVal k="Model" v={data.ai_generation.model} />
        <KeyVal k="Language" v={(data.ai_generation.language || '').toUpperCase()} />
        <KeyVal k="Evidence chunks" v={data.ai_generation.chunks_used} />
        {data.ai_generation.style_examples_used > 0 && (
          <KeyVal k="Style examples" v={data.ai_generation.style_examples_used} />
        )}
        {data.ai_generation.previous_chapters_used > 0 && (
          <KeyVal k="Prior chapters" v={data.ai_generation.previous_chapters_used} />
        )}
        <KeyVal k="Generated" v={fmtDate(data.ai_generation.generated_at)} />
        {data.ai_generation.last_edited_at && data.ai_generation.last_edited_at !== data.ai_generation.generated_at && (
          <KeyVal k="Last edit" v={fmtDate(data.ai_generation.last_edited_at)} />
        )}
      </Section>

      {/* Citations card */}
      <Section title="Citations" icon={Quote}>
        <Pct label="Verified" value={data.citations.verification_pct} totalSuffix={`${data.citations.verified}/${data.citations.total}`} color="emerald" />
        <div className="grid grid-cols-3 gap-2 mt-2 text-center text-[11px]">
          <Stat n={data.citations.verified} label="verified" color="emerald" />
          <Stat n={data.citations.suspect} label="needs review" color="amber" />
          <Stat n={data.citations.fabricated} label="unresolved" color="rose" />
        </div>
      </Section>

      {/* Paragraphs supported */}
      <Section title="Paragraph support" icon={FileText}>
        <Pct
          label="Cited paragraphs"
          value={data.paragraphs.supported_pct}
          totalSuffix={`${data.paragraphs.total - data.paragraphs.unsupported}/${data.paragraphs.total}`}
          color={data.paragraphs.supported_pct >= 80 ? 'emerald' : data.paragraphs.supported_pct >= 50 ? 'amber' : 'rose'}
        />
        {data.paragraphs.unsupported > 0 && (
          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
            <AlertTriangle className="w-3 h-3 inline text-amber-500 mr-1" />
            {data.paragraphs.unsupported} paragraph{data.paragraphs.unsupported === 1 ? '' : 's'} have no citation token.
          </p>
        )}
      </Section>

      {/* Compliance card */}
      {data.compliance && (
        <Section title="EQUATOR compliance" icon={BadgeCheck}>
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{data.compliance.guideline_id}</span>
            <span className="font-serif text-2xl font-semibold text-[#1d4ed8] dark:text-[#7aa9ff]" style={{ fontFamily: '"Fraunces", serif' }}>
              {data.compliance.overall_score}<span className="text-sm text-muted-foreground">/100</span>
            </span>
          </div>
          {data.compliance.summary && (
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed line-clamp-3">
              {data.compliance.summary}
            </p>
          )}
          <p className="text-[10px] text-muted-foreground mt-2">Checked {fmtDate(data.compliance.checked_at)}</p>
        </Section>
      )}

      {/* Safety card */}
      <Section
        title="Claim safety"
        icon={data.safety?.classification === 'safe' ? ShieldCheck :
              data.safety?.classification === 'high_risk' ? AlertTriangle : ShieldAlert}
        action={
          <Button size="sm" variant="ghost" onClick={runSafety} disabled={safetyChecking} className="h-7 text-[11px]"
            data-testid="run-safety-btn">
            {safetyChecking ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            {data.safety ? 'Re-check' : 'Run check'}
          </Button>
        }
      >
        {!data.safety ? (
          <p className="text-[11px] text-muted-foreground">
            No safety check run yet. Use the button to scan this chapter for
            clinical-decision language.
          </p>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <span className={`text-[10px] uppercase tracking-widest font-semibold ${
                data.safety.classification === 'safe' ? 'text-emerald-600'
                : data.safety.classification === 'high_risk' ? 'text-rose-600'
                : 'text-amber-600'
              }`}>{data.safety.classification.replace('_',' ')}</span>
              <span className="font-mono text-xs">risk · {data.safety.score}/100</span>
            </div>
            {data.safety.reason && (
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed line-clamp-3">
                {data.safety.reason}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground mt-2">Checked {fmtDate(data.safety.checked_at)}</p>
          </>
        )}
      </Section>

      {/* Version history */}
      <Section title="Version history" icon={History}>
        <KeyVal k="Snapshots" v={data.version_history.snapshots} />
      </Section>

      {/* Latest reviewer */}
      {data.latest_review?.exists && (
        <Section title="Reviewer mode" icon={Database}>
          <KeyVal k="Last score" v={`${data.latest_review.score ?? '—'}/100`} />
        </Section>
      )}

      {/* Approve / revoke */}
      <div className="pt-2 border-t border-border">
        <Button
          onClick={toggleApprove}
          variant={data.human_review?.approved ? 'outline' : 'default'}
          className={`w-full ${data.human_review?.approved
            ? ''
            : 'bg-[#0b1a35] hover:bg-[#1d4ed8] text-white'}`}
          data-testid="approve-chapter-btn"
        >
          {data.human_review?.approved ? (
            <><Lock className="w-4 h-4 mr-1.5" /> Approved — click to revoke</>
          ) : (
            <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark as human-reviewed</>
          )}
        </Button>
        {data.human_review?.approved && (
          <p className="text-[11px] text-muted-foreground mt-2 text-center">
            By {data.human_review.approved_by} · {fmtDate(data.human_review.approved_at)}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny presentational helpers
// ---------------------------------------------------------------------------
function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="bg-muted/40 dark:bg-white/[0.02] rounded-lg border border-border p-3.5">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-[#1d4ed8] dark:text-[#7aa9ff]" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function KeyVal({ k, v }) {
  return (
    <div className="flex items-center justify-between text-xs py-0.5">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono text-foreground">{v ?? '—'}</span>
    </div>
  );
}

function Pct({ label, value, totalSuffix, color = 'emerald' }) {
  const bar = {
    emerald: 'bg-emerald-500',
    amber:   'bg-amber-500',
    rose:    'bg-rose-500',
  }[color] || 'bg-emerald-500';
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{value}% <span className="text-muted-foreground">({totalSuffix})</span></span>
      </div>
      <div className="h-1.5 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
        <div className={`${bar} h-full transition-all`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function Stat({ n, label, color }) {
  const c = {
    emerald: 'text-emerald-600',
    amber:   'text-amber-600',
    rose:    'text-rose-600',
  }[color];
  return (
    <div>
      <div className={`font-semibold ${c}`}>{n}</div>
      <div className="text-muted-foreground uppercase tracking-widest text-[9px]">{label}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
